import { motion } from 'framer-motion';
import { FiLock, FiSend } from 'react-icons/fi';

import { useContactForm } from '../hooks/useContactForm';
import type { ContactFormValues } from '../types/contactTypes';

export default function ContactFormSection() {
  const {
    values,
    fieldErrors,
    status,
    errorMessage,
    setFieldValue,
    handleSubmit,
  } = useContactForm();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFieldValue(e.target.name as keyof ContactFormValues, e.target.value);
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-4 py-3 rounded-lg border ${
      hasError ? 'border-red-500' : 'border-theme'
    } bg-card dark:bg-panel text-theme-primary dark:text-theme-primary focus:outline-none focus:ring-2 focus:ring-secondary ring-offset-surface`;

  const isLoading = status === 'loading';

  return (
    <section className="section bg-card dark:bg-panel/80">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary dark:text-theme-primary mb-6">
              send us a <span className="text-secondary italic">message</span>
            </h2>
            <p className="text-lg text-theme-secondary mb-6">
              Fill in the form and someone from our team will get back to you
              shortly. No bots, no automated replies — just real people who
              care.
            </p>
            <span className="bg-brand-primary/10 text-secondary text-sm px-4 py-2 rounded-full inline-flex items-center">
              Typical response: within 24 hours
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="first_name"
                    className="block text-sm font-medium text-theme-secondary mb-1"
                  >
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={values.first_name}
                    onChange={handleChange}
                    placeholder="First name"
                    className={inputClass(!!fieldErrors.first_name)}
                  />
                  {fieldErrors.first_name && (
                    <p className="mt-1 text-sm text-red-500">
                      {fieldErrors.first_name}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="last_name"
                    className="block text-sm font-medium text-theme-secondary mb-1"
                  >
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={values.last_name}
                    onChange={handleChange}
                    placeholder="Last name"
                    className={inputClass(!!fieldErrors.last_name)}
                  />
                  {fieldErrors.last_name && (
                    <p className="mt-1 text-sm text-red-500">
                      {fieldErrors.last_name}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-theme-secondary mb-1"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className={inputClass(!!fieldErrors.email)}
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-sm text-red-500">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-theme-secondary mb-1"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={values.message}
                  onChange={handleChange}
                  placeholder="How can we help?"
                  className={inputClass(!!fieldErrors.message)}
                />
                {fieldErrors.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {fieldErrors.message}
                  </p>
                )}
              </div>

              {errorMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-lg"
                >
                  {errorMessage}
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full btn-primary flex justify-center items-center"
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <>
                    <FiSend className="mr-2" />
                    Send message
                  </>
                )}
              </motion.button>

              {status === 'success' && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  role="status"
                  className="text-sm text-green-700 dark:text-green-400 text-center"
                >
                  Message sent successfully. We'll get back to you soon.
                </motion.p>
              )}

              <p className="text-xs text-theme-secondary text-center flex items-center justify-center">
                <FiLock className="mr-1" />
                Your info is private and secure
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
